import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { BookingColumnKeys } from 'Views/Components/_HumanWritten/StaffTables/StaffBookings/BookingColumnKeys';
import { Checkbox } from 'Views/Components/Checkbox/Checkbox';
import Popover from 'Views/Components/_HumanWritten/Popover';
import { useStaffBookingsStore, ColumnConfig } from './StaffBookingsFilterStore';

export const StaffBookingsColumnSelector = () => {
	const intl = useIntl();
	const { visibleColumns, setVisibleColumns } = useStaffBookingsStore();

	// Helper function to translate columns
	const translateColumns = (columns: ColumnConfig[]): ColumnConfig[] => {
		const columnTranslations: Record<string, { id: string; defaultMessage: string }> = {
			[BookingColumnKeys.REGO]: {
				id: 'vehicle-tab__rego__label',
				defaultMessage: 'Rego',
			},
			// Add more translations here as needed
		};

		return columns.map(col => {
			const translation = columnTranslations[col.key];
			return {
				...col,
				label: translation
					? intl.formatMessage({ id: translation.id, defaultMessage: translation.defaultMessage })
					: col.label,
			};
		});
	};

	// Translate columns for display
	const translatedColumns = translateColumns(visibleColumns);
	const [tempColumns, setTempColumns] = useState<ColumnConfig[]>(translatedColumns);

	// Sync temp columns when visibleColumns changes
	useEffect(() => {
		setTempColumns(translateColumns(visibleColumns));
	}, [visibleColumns, intl]);

	const visibleCount = tempColumns.filter(col => col.visible && !col.locked).length;
	const totalCount = tempColumns.filter(col => !col.locked).length;

	const onAfterClose = () => {
		const hasChanges = tempColumns.some((col, idx) => col.visible !== visibleColumns[idx].visible);

		if (hasChanges) {
			setVisibleColumns(tempColumns);
		}
	};

	const handleToggle = (key: string) => {
		setTempColumns(prev =>
			prev.map(col => (col.key === key && !col.locked ? { ...col, visible: !col.visible } : col)),
		);
	};

	const handleSelectAll = () => {
		setTempColumns(prev => prev.map(col => ({ ...col, visible: true })));
	};

	const isAllColumnsSelected = visibleCount === totalCount;

	const handleDeselectAll = () => {
		setTempColumns(prev => prev.map(col => (col.locked ? col : { ...col, visible: false })));
	};

	return (
		<Popover
			popoverClassName="fh-popover column-selector-popover"
			buttonTestId="staff-bookings-columns"
			onAfterClose={onAfterClose}
			buttonContent={(() => {
				return (
					<>
						<p className="grey">Columns</p>
						<span>
							{visibleCount}/{totalCount}
						</span>
					</>
				);
			})()}
			popoverContent={() => (
				<>
					<Checkbox
						className="fh-popover__checkbox fh-popover__list-item"
						model={{
							value: isAllColumnsSelected,
						}}
						modelProperty="value"
						label="All"
						onChecked={(_, value) => {
							if (!value) {
								handleDeselectAll();
							} else {
								handleSelectAll();
							}
						}}
					/>
					<div className="column-selector__list">
						{tempColumns.map(column => (
							<Checkbox
								key={column.key}
								className="fh-popover__checkbox fh-popover__list-item"
								model={{ value: column.visible }}
								modelProperty="value"
								label={column.label}
								isReadOnly={column.locked}
								inputProps={{ 'data-test-id': `column-selector-${column.key}` } as any}
								onChecked={(_, value) => handleToggle(column.key)}
							/>
						))}
					</div>
				</>
			)}
		/>
	);
};
